<template>
  <div>
    <div class="base-create">
      <div class="base-create-header">
        <h1>
          <span class="circle-step">1</span>
          <span>
            {{ $t('classes.create_class_flow.class_overview.details') }}
            <el-tooltip :content="$t('classes.create_class_flow.class_details.team_detail_tooltip')">
              <i class="el-icon-warning-outline" />
            </el-tooltip>
          </span>
        </h1>
      </div>
      <div class="create-class-overview">
        <el-row>
          <el-col :span="12">
            <info-row :label="$t($i18nLabelsHelper(organizationType, 'group_name'))" :value="classDetails.className" />
          </el-col>
          <el-col :span="12">
            <info-row :label="$t('classes.create_class_flow.class_overview.start_end_date')" :value="startEndDate" />
          </el-col>
        </el-row>
        <div class="row class-teacher">
          <info-row :label="$t($i18nLabelsHelper(organizationType, 'group_teacher'))">
            <div class="user-info">
              <span v-if="!isTeacherAndAdmin" class="name">{{ currentUser.first_name }} {{ currentUser.last_name }}</span>
              <span>{{ isTeacherAndAdmin ? classDetails.teacher : currentUser.email }}</span>
            </div>
          </info-row>
        </div>
      </div>
    </div>
    <hr class="line-separate" />
    <div class="base-create">
      <div class="base-create-header">
        <h1>
          <span class="circle-step">2</span>
          <span>
            {{ $t($i18nLabelsHelper(this.organizationType, 'goals.engage_students')) }}
          </span>
        </h1>
      </div>
      <div>
        <el-row type="flex" justify="space-between">
          <el-col :md="11" :xs="24" class="practice">
            <el-card class="box-card" shadow="never">
              <div class="card-number">
                <span><img src="@/assets/images/task-list.svg" /></span>
              </div>
              <div class="card-content">
                <div class="description">
                  <div class="input-wrapper">
                    {{ $t('classes.create_class_flow.class_created.goals_section.practice_at_least') }}
                    <span class="goal-value">
                      {{ classDetails.daysPractice }}
                    </span>
                    {{ $t('classes.create_class_flow.class_created.goals_section.days_week') }}
                  </div>
                  <p>
                    {{ $t('classes.create_class_flow.class_created.goals_section.keep_them_engaged') }}
                  </p>
                </div>
              </div>
            </el-card>
          </el-col>
          <el-col :md="11" :xs="24" class="practice">
            <el-card class="box-card" shadow="never">
              <div class="card-number">
                <span><img src="@/assets/images/clock.svg" /></span>
              </div>
              <div class="card-content">
                <div class="description">
                  <div class="input-wrapper">
                    {{ $t('classes.create_class_flow.class_created.goals_section.practice') }}
                    <span class="goal-value">{{ classDetails.minutesPractice }}</span>
                    {{ $t('classes.create_class_flow.class_created.goals_section.minutes_day') }}
                  </div>
                  <p>
                    {{ $t('classes.create_class_flow.class_created.goals_section.studies_review') }}
                  </p>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </div>
    <hr class="line-separate" />
    <div class="base-create">
      <div class="base-create-header">
        <h1>
          <span class="circle-step">3</span>
          <span> {{ $t('classes.create_class_flow.class_created.goals_section.learners') }} </span>
        </h1>
      </div>
      <div>
        <el-table :data="users" style="width: 100%">
          <el-table-column prop="text" :label="$t('classes.create_class_flow.class_created.goals_section.table.email')" width="300">
            <template slot-scope="scope">
              <div class="owner-info">
                <p>{{ scope.row.text }}</p>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="text" :label="$t('classes.create_class_flow.class_created.goals_section.table.first_name')" width="150">
            <template slot-scope="scope">
              <div class="owner-info">
                <p>{{ scope.row.first_name }}</p>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="text" :label="$t('classes.create_class_flow.class_created.goals_section.table.last_name')" width="150">
            <template slot-scope="scope">
              <div class="owner-info">
                <p>{{ scope.row.last_name }}</p>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="text" :label="$t('classes.create_class_flow.class_created.goals_section.table.license')">
            <template slot-scope="scope">
              <p v-if="scope.row.plan_id">{{ getLabelPlan(scope.row.plan_id) }}</p>
              <p v-else>{{ $t('classes.create_class_flow.class_created.goals_section.table.invite_only') }}</p>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    <display-students
      :dialogVisible="showStudentsModal"
      v-on:closed="showStudentsModal = false"
      :users="users"
      @update:users="$emit('update:users', $event)"
      :title="$t($i18nLabelsHelper(organizationType, 'students'))"
      :noteConsent="$t($i18nLabelsHelper(organizationType, 'student_consent_to_class'), { numberOfUsers })"
    />
    <import-invite-users
      :dialogVisible="showImportModal"
      v-on:closed="showImportModal = false"
      :title="$t($i18nLabelsHelper(organizationType, 'add_students'))"
      :organizationType="organizationType"
      :organizationId="organizationId"
      :classId="classId"
      @update:uploadId="$emit('update:uploadId', $event)"
    />
  </div>
</template>

<script>
import InfoRow from '@/components/common/InfoRow'
// import Chip from "@/components/common/Chip";
import DisplayStudents from '@/components/modals/classes/DisplayStudents'
import ImportInviteUsers from '@/components/modals/classes/ImportInviteUsers'
import { pathOr, isNil, compose, propOr, length } from 'ramda'
import { mapActions, mapGetters } from 'vuex'
import { memberships } from '@/helpers/memberships'

export default {
  components: {
    InfoRow,
    DisplayStudents,
    ImportInviteUsers
  },
  props: {
    classDetails: {},
    organizationType: {},
    editClass: {
      type: Function,
      default: () => {}
    },
    showEditClass: {
      type: Boolean,
      default: true
    },
    showUserInfo: {
      type: Boolean,
      default: true
    },
    organizationId: {},
    uploadId: {
      default: null
    }
  },
  data() {
    return {
      showStudentsModal: false,
      showImportModal: false,
      uploadDetails: {}
    }
  },
  computed: {
    startEndDate() {
      const not_selected = this.$t('classes.create_class_flow.class_overview.not_selected')
      const start_date = isNil(this.classDetails.startDate) ? not_selected : new Date(this.classDetails.startDate).toDateString()
      const end_date = isNil(this.classDetails.endDate) ? not_selected : new Date(this.classDetails.endDate).toDateString()

      return `${start_date} - ${end_date}`
    },
    classId() {
      return pathOr(null, ['classDetails', 'id'])(this)
    },
    ...mapGetters({
      isTeacher: 'organization/isOrganizationTeacher',
      isAdmin: 'organization/isOrganizationAdmin',
      isAssociateAdmin: 'organization/isOrganizationAssociateAdmin',
      currentUser: 'user/getUser'
    }),
    isTeacherAndAdmin() {
      return this.isAdmin || !this.isTeacher
    },
    users() {
      // if (this.uploadId) {
      //   return compose(
      //     map(objOf("text")),
      //     pathOr([], ["uploadDetails", "emails"])
      //   )(this);
      // }
      return pathOr([], ['classDetails', 'users'])(this)
    },
    avaiMemberships() {
      return pathOr([], ['classDetails', 'avaiMemberships'])(this)
    },
    numberOfUsers: compose(length, propOr([], 'users'))
  },
  methods: {
    getLabelPlan(plan_id) {
      return this.avaiMemberships.find(item => item.value == plan_id)["label"];
    },
    ...mapActions('invite', ['postSentInviteUploadEmails', 'patchInviteUploadEmails', 'getInviteUploadEmails'])
  },
  watch: {
    // uploadId: {
    //   immediate: true,
    //   deep: true,
    //   async handler(newUploadId) {
    //     if (newUploadId) {
    //       const response = await this.getInviteUploadEmails({
    //         uploadId: this.uploadId
    //       });
    //       this.uploadDetails = propOr([], "data")(response);
    //     }
    //   }
    // }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../scss/_colors.scss';
h3 {
  text-transform: none;
}
.class-teacher {
  margin-top: 40px;
  .user-info {
    display: flex;
    flex-direction: column;
    .name {
      color: $black;
    }
  }
}
p {
  font-weight: 300;
  font-size: 18px;
  color: $dark-blue-2;
  margin: 0;
}
.practice {
  display: flex;
  align-items: center;
  margin-top: 24px;
  .input-wrapper {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    color: $black;
    .goal-value {
      font-weight: bold;
      font-size: 20px;
      color: #478dff;
      padding: 0 5px;
    }
  }
  .description {
    p {
      margin-top: 5px;
      font-size: 16px;
      line-height: 24px;
      opacity: 0.7;
    }
    .assessment-test-title {
      font-weight: 500;
      font-size: 20px;
      line-height: 24px;
      color: $black;
      opacity: 1;
    }
  }
  .box-card {
    border: 1px solid #e6e6e6;
    box-sizing: border-box;
    border-radius: 16px;
    // margin-bottom: 24px;
    height: 214px;
    width:100%;
    ::v-deep .el-card__body {
      align-items: flex-start;
      .card-number {
        margin-bottom: 8px;
      }
      .card-content {
        flex: 1 0;

        h2 {
          font-size: 24px;
          line-height: 36px;
          color: #121131;
        }

        p {
          font-size: 16px;
          line-height: 24px;
          color: #121131;
        }

        button {
          float: right;
        }
      }
    }
  }
}
.line-separate {
  border: 1px solid#E6E6E6;
  margin: 40px 0 32px;
}
</style>
